/** Unidades IFC: o que o projecto declara, e a unidade de cada
 *  propriedade.
 *
 *  Num IFC, uma propriedade numérica pode trazer a sua unidade
 *  (`IfcPropertySingleValue.Unit`, `IfcQuantityLength.Unit`, ...). Se não
 *  trouxer, vale a do projecto para esse tipo de medida
 *  (`IfcProject.UnitsInContext`). Se o projecto também não declarar, a
 *  unidade fica indefinida: o valor "3000" tanto pode ser milímetros
 *  como metros, e é isso que interessa saber.
 *
 *  Puro: recebe as entidades como o web-ifc as devolve (`GetLine` com
 *  as referências resolvidas) e devolve texto para mostrar.
 */

export type ClasseDeUnidade = 'SI' | 'conversao' | 'derivada' | 'monetaria' | 'contexto'

export interface Unidade {
  /** LENGTHUNIT, AREAUNIT, ..., ou USERDEFINED:<nome> nas derivadas
   *  definidas pelo autor. */
  tipo: string
  classe: ClasseDeUnidade
  simbolo: string
  nome: string
  /** O que a define, quando não é SI: "1 ° = 0,0174533 rad". */
  detalhe?: string
  /** A entidade do ficheiro de onde veio, para se poder conferir no IFC:
   *  { id: 65, classe: 'IfcDerivedUnit' } é a linha #65. */
  origem?: { id: number; classe: string }
  /** Só nas derivadas: a forma habitual, calculada pelas dimensões de
   *  base (kg·K⁻¹·s⁻³ é W/(m²·K)). Só existe quando a conta fecha
   *  exactamente, sem factor de escala. Não vem do ficheiro. */
  equivalencia?: string
}

/** "#65 IfcDerivedUnit": onde procurar a unidade no ficheiro. */
export function textoDaOrigem(u: Unidade): string {
  return u.origem ? `#${u.origem.id} ${u.origem.classe}` : ''
}

/** O valor de um atributo do web-ifc: vem embrulhado de várias formas. */
function valor(x: unknown): unknown {
  if (x === null || x === undefined) return null
  if (typeof x !== 'object') return x
  const o = x as Record<string, unknown>
  if ('value' in o) return o.value
  if ('_representationValue' in o) return o._representationValue
  if ('_internalValue' in o) return o._internalValue
  return null
}

function texto(x: unknown): string {
  const v = valor(x)
  return v === null || v === undefined ? '' : String(v)
}

const PREFIXOS: Record<string, [string, string]> = {
  EXA: ['E', 'exa'],
  PETA: ['P', 'peta'],
  TERA: ['T', 'tera'],
  GIGA: ['G', 'giga'],
  MEGA: ['M', 'mega'],
  KILO: ['k', 'quilo'],
  HECTO: ['h', 'hecto'],
  DECA: ['da', 'deca'],
  DECI: ['d', 'deci'],
  CENTI: ['c', 'centi'],
  MILLI: ['m', 'mili'],
  MICRO: ['µ', 'micro'],
  NANO: ['n', 'nano'],
  PICO: ['p', 'pico'],
  FEMTO: ['f', 'femto'],
  ATTO: ['a', 'atto'],
}

const SI: Record<string, [string, string]> = {
  METRE: ['m', 'metro'],
  SQUARE_METRE: ['m²', 'metro quadrado'],
  CUBIC_METRE: ['m³', 'metro cúbico'],
  GRAM: ['g', 'grama'],
  SECOND: ['s', 'segundo'],
  AMPERE: ['A', 'ampere'],
  KELVIN: ['K', 'kelvin'],
  DEGREE_CELSIUS: ['°C', 'grau Celsius'],
  MOLE: ['mol', 'mole'],
  CANDELA: ['cd', 'candela'],
  RADIAN: ['rad', 'radiano'],
  STERADIAN: ['sr', 'esterradiano'],
  HERTZ: ['Hz', 'hertz'],
  NEWTON: ['N', 'newton'],
  PASCAL: ['Pa', 'pascal'],
  JOULE: ['J', 'joule'],
  WATT: ['W', 'watt'],
  COULOMB: ['C', 'coulomb'],
  VOLT: ['V', 'volt'],
  FARAD: ['F', 'farad'],
  OHM: ['Ω', 'ohm'],
  SIEMENS: ['S', 'siemens'],
  WEBER: ['Wb', 'weber'],
  TESLA: ['T', 'tesla'],
  HENRY: ['H', 'henry'],
  LUMEN: ['lm', 'lúmen'],
  LUX: ['lx', 'lux'],
  BECQUEREL: ['Bq', 'becquerel'],
  GRAY: ['Gy', 'gray'],
  SIEVERT: ['Sv', 'sievert'],
}

/** Nomes como se dizem, para os casos em que juntar prefixo e nome
 *  daria errado ("milimetro", "quilometro"). */
const NOMES_FEITOS: Record<string, string> = {
  'MILLI METRE': 'milímetro',
  'CENTI METRE': 'centímetro',
  'DECI METRE': 'decímetro',
  'KILO METRE': 'quilómetro',
  'MILLI SQUARE_METRE': 'milímetro quadrado',
  'CENTI SQUARE_METRE': 'centímetro quadrado',
  'MILLI CUBIC_METRE': 'milímetro cúbico',
  'CENTI CUBIC_METRE': 'centímetro cúbico',
  'KILO GRAM': 'quilograma',
}

/** O símbolo de uma unidade SI com prefixo. As de área e volume com
 *  prefixo aplicam-no ao metro: MILLI SQUARE_METRE é mm², e não m·m². */
export function simboloSI(prefixo: string, nome: string): string {
  const p = PREFIXOS[prefixo]?.[0] ?? ''
  return `${p}${SI[nome]?.[0] ?? nome.toLowerCase()}`
}

function nomeSI(prefixo: string, nome: string): string {
  const feito = NOMES_FEITOS[`${prefixo} ${nome}`]
  if (feito) return feito
  const p = PREFIXOS[prefixo]?.[1] ?? ''
  return `${p}${SI[nome]?.[1] ?? nome.toLowerCase()}`
}

/** Unidades antigas por conversão, que os programas anglo-saxónicos
 *  escrevem. O nome no IFC é livre; estes são os que aparecem. */
const CONVERSOES: Record<string, [string, string]> = {
  DEGREE: ['°', 'grau'],
  GRAD: ['gon', 'grado'],
  FOOT: ['ft', 'pé'],
  INCH: ['in', 'polegada'],
  YARD: ['yd', 'jarda'],
  MILE: ['mi', 'milha'],
  'SQUARE FOOT': ['ft²', 'pé quadrado'],
  'SQUARE INCH': ['in²', 'polegada quadrada'],
  'CUBIC FOOT': ['ft³', 'pé cúbico'],
  'CUBIC INCH': ['in³', 'polegada cúbica'],
  POUND: ['lb', 'libra'],
  'POUND-FORCE': ['lbf', 'libra-força'],
  GALLON: ['gal', 'galão'],
  LITRE: ['L', 'litro'],
  MINUTE: ['min', 'minuto'],
  HOUR: ['h', 'hora'],
  DAY: ['d', 'dia'],
}

const EXPOENTES: Record<string, string> = { '-': '⁻', '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴', '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹' }

function expoente(n: number): string {
  if (n === 1) return ''
  return String(n)
    .split('')
    .map((c) => EXPOENTES[c] ?? c)
    .join('')
}

/** O que cada tipo de unidade mede, em português. */
export const NOME_DO_TIPO: Record<string, string> = {
  LENGTHUNIT: 'comprimento',
  AREAUNIT: 'área',
  VOLUMEUNIT: 'volume',
  PLANEANGLEUNIT: 'ângulo plano',
  SOLIDANGLEUNIT: 'ângulo sólido',
  MASSUNIT: 'massa',
  TIMEUNIT: 'tempo',
  THERMODYNAMICTEMPERATUREUNIT: 'temperatura',
  ELECTRICCURRENTUNIT: 'corrente eléctrica',
  ELECTRICVOLTAGEUNIT: 'tensão eléctrica',
  ELECTRICRESISTANCEUNIT: 'resistência eléctrica',
  ELECTRICCAPACITANCEUNIT: 'capacidade eléctrica',
  ELECTRICCHARGEUNIT: 'carga eléctrica',
  ELECTRICCONDUCTANCEUNIT: 'condutância eléctrica',
  POWERUNIT: 'potência',
  FORCEUNIT: 'força',
  PRESSUREUNIT: 'pressão',
  ENERGYUNIT: 'energia',
  FREQUENCYUNIT: 'frequência',
  ILLUMINANCEUNIT: 'iluminância',
  LUMINOUSFLUXUNIT: 'fluxo luminoso',
  LUMINOUSINTENSITYUNIT: 'intensidade luminosa',
  AMOUNTOFSUBSTANCEUNIT: 'quantidade de substância',
  MAGNETICFLUXUNIT: 'fluxo magnético',
  MAGNETICFLUXDENSITYUNIT: 'densidade de fluxo magnético',
  INDUCTANCEUNIT: 'indutância',
  RADIOACTIVITYUNIT: 'actividade radioactiva',
  ABSORBEDDOSEUNIT: 'dose absorvida',
  DOSEEQUIVALENTUNIT: 'dose equivalente',
  MASSDENSITYUNIT: 'massa volúmica',
  MOMENTOFINERTIAUNIT: 'momento de inércia',
  THERMALTRANSMITTANCEUNIT: 'coeficiente de transmissão térmica',
  THERMALCONDUCTANCEUNIT: 'condutibilidade térmica',
  THERMALRESISTANCEUNIT: 'resistência térmica',
  VOLUMETRICFLOWRATEUNIT: 'caudal volúmico',
  MASSFLOWRATEUNIT: 'caudal mássico',
  LINEARVELOCITYUNIT: 'velocidade',
  LINEARFORCEUNIT: 'força por comprimento',
  PLANARFORCEUNIT: 'força por área',
  LINEARMOMENTUNIT: 'momento por comprimento',
  TORQUEUNIT: 'momento',
  HEATFLUXDENSITYUNIT: 'densidade de fluxo de calor',
  SPECIFICHEATCAPACITYUNIT: 'calor específico',
  DYNAMICVISCOSITYUNIT: 'viscosidade dinâmica',
  MODULUSOFELASTICITYUNIT: 'módulo de elasticidade',
  SOUNDPOWERUNIT: 'potência sonora',
  SOUNDPRESSUREUNIT: 'pressão sonora',
  MONETARYUNIT: 'moeda',
}

export function nomeDoTipo(tipo: string): string {
  if (tipo.startsWith('USERDEFINED:')) return tipo.slice('USERDEFINED:'.length)
  return NOME_DO_TIPO[tipo] ?? tipo.replace(/UNIT$/, '').toLowerCase()
}

// ---------------------------------------------------------------------------
// Equivalência das derivadas, por análise dimensional.
//
// O Revit escreve a transmissão térmica como kg·K⁻¹·s⁻³, que é o W/(m²·K)
// de sempre em unidades de base. Reduz-se cada unidade SI às dimensões de
// base (kg, m, s, K, A, mol, cd) e a um factor de escala (o prefixo);
// soma-se com os expoentes; e procura-se uma forma habitual com as mesmas
// dimensões. Só se mostra quando o factor dá exactamente 1: um mm·s⁻¹
// não é m/s, e dizê-lo seria inventar.
// ---------------------------------------------------------------------------

type Base = 'kg' | 'm' | 's' | 'K' | 'A' | 'mol' | 'cd'
type Dimensoes = Partial<Record<Base, number>>

/** Dimensões e escala de cada unidade SI (a do grama é 1e-3 kg). O
 *  radiano e o esterradiano não têm dimensão. O grau Celsius fica de
 *  fora: tem desvio de origem, e uma derivada com ele não se reduz a
 *  kelvin sem se decidir se é uma diferença de temperatura. */
const SI_EM_BASE: Record<string, [Dimensoes, number] | null> = {
  METRE: [{ m: 1 }, 1],
  SQUARE_METRE: [{ m: 2 }, 1],
  CUBIC_METRE: [{ m: 3 }, 1],
  GRAM: [{ kg: 1 }, 1e-3],
  SECOND: [{ s: 1 }, 1],
  KELVIN: [{ K: 1 }, 1],
  DEGREE_CELSIUS: null,
  AMPERE: [{ A: 1 }, 1],
  MOLE: [{ mol: 1 }, 1],
  CANDELA: [{ cd: 1 }, 1],
  RADIAN: [{}, 1],
  STERADIAN: [{}, 1],
  HERTZ: [{ s: -1 }, 1],
  NEWTON: [{ kg: 1, m: 1, s: -2 }, 1],
  PASCAL: [{ kg: 1, m: -1, s: -2 }, 1],
  JOULE: [{ kg: 1, m: 2, s: -2 }, 1],
  WATT: [{ kg: 1, m: 2, s: -3 }, 1],
  COULOMB: [{ A: 1, s: 1 }, 1],
  VOLT: [{ kg: 1, m: 2, s: -3, A: -1 }, 1],
  OHM: [{ kg: 1, m: 2, s: -3, A: -2 }, 1],
  SIEMENS: [{ kg: -1, m: -2, s: 3, A: 2 }, 1],
  FARAD: [{ kg: -1, m: -2, s: 4, A: 2 }, 1],
  WEBER: [{ kg: 1, m: 2, s: -2, A: -1 }, 1],
  TESLA: [{ kg: 1, s: -2, A: -1 }, 1],
  HENRY: [{ kg: 1, m: 2, s: -2, A: -2 }, 1],
  LUMEN: [{ cd: 1 }, 1],
  LUX: [{ cd: 1, m: -2 }, 1],
  BECQUEREL: [{ s: -1 }, 1],
  GRAY: [{ m: 2, s: -2 }, 1],
  SIEVERT: [{ m: 2, s: -2 }, 1],
}

const EXPOENTE_DO_PREFIXO: Record<string, number> = {
  EXA: 18, PETA: 15, TERA: 12, GIGA: 9, MEGA: 6, KILO: 3, HECTO: 2, DECA: 1,
  DECI: -1, CENTI: -2, MILLI: -3, MICRO: -6, NANO: -9, PICO: -12, FEMTO: -15, ATTO: -18,
}

/** As formas habituais. As que têm `tipos` usam-se primeiro para esses
 *  tipos de unidade; as outras servem para qualquer derivada. */
const FORMAS_HABITUAIS: Array<{ simbolo: string; dim: Dimensoes; tipos?: string[] }> = [
  { simbolo: 'W/(m²·K)', dim: { kg: 1, s: -3, K: -1 }, tipos: ['THERMALTRANSMITTANCEUNIT'] },
  { simbolo: 'W/(m·K)', dim: { kg: 1, m: 1, s: -3, K: -1 }, tipos: ['THERMALCONDUCTANCEUNIT'] },
  { simbolo: 'm²·K/W', dim: { kg: -1, s: 3, K: 1 }, tipos: ['THERMALRESISTANCEUNIT'] },
  { simbolo: 'W/m²', dim: { kg: 1, s: -3 }, tipos: ['HEATFLUXDENSITYUNIT'] },
  { simbolo: 'J/(kg·K)', dim: { m: 2, s: -2, K: -1 }, tipos: ['SPECIFICHEATCAPACITYUNIT'] },
  { simbolo: 'kg/m³', dim: { kg: 1, m: -3 }, tipos: ['MASSDENSITYUNIT'] },
  { simbolo: 'm³/s', dim: { m: 3, s: -1 }, tipos: ['VOLUMETRICFLOWRATEUNIT'] },
  { simbolo: 'kg/s', dim: { kg: 1, s: -1 }, tipos: ['MASSFLOWRATEUNIT'] },
  { simbolo: 'm/s', dim: { m: 1, s: -1 }, tipos: ['LINEARVELOCITYUNIT'] },
  { simbolo: 'N/m', dim: { kg: 1, s: -2 }, tipos: ['LINEARFORCEUNIT'] },
  { simbolo: 'N/m²', dim: { kg: 1, m: -1, s: -2 }, tipos: ['PLANARFORCEUNIT'] },
  { simbolo: 'Pa', dim: { kg: 1, m: -1, s: -2 }, tipos: ['MODULUSOFELASTICITYUNIT'] },
  { simbolo: 'Pa·s', dim: { kg: 1, m: -1, s: -1 }, tipos: ['DYNAMICVISCOSITYUNIT'] },
  { simbolo: 'N·m', dim: { kg: 1, m: 2, s: -2 }, tipos: ['TORQUEUNIT'] },
  { simbolo: 'N·m/m', dim: { kg: 1, m: 1, s: -2 }, tipos: ['LINEARMOMENTUNIT'] },
  { simbolo: 'm⁴', dim: { m: 4 }, tipos: ['MOMENTOFINERTIAUNIT'] },
  { simbolo: 'lm/W', dim: { cd: 1, kg: -1, m: -2, s: 3 } },
  { simbolo: 'N', dim: { kg: 1, m: 1, s: -2 } },
  { simbolo: 'Pa', dim: { kg: 1, m: -1, s: -2 } },
  { simbolo: 'J', dim: { kg: 1, m: 2, s: -2 } },
  { simbolo: 'W', dim: { kg: 1, m: 2, s: -3 } },
  { simbolo: 'V', dim: { kg: 1, m: 2, s: -3, A: -1 } },
  { simbolo: 'Ω', dim: { kg: 1, m: 2, s: -3, A: -2 } },
]

const BASES: Base[] = ['kg', 'm', 's', 'K', 'A', 'mol', 'cd']

function mesmasDimensoes(a: Dimensoes, b: Dimensoes): boolean {
  return BASES.every((k) => (a[k] ?? 0) === (b[k] ?? 0))
}

/** Uma unidade SI (entidade do web-ifc) reduzida às dimensões de base e
 *  a uma escala. Null quando não se reduz (grau Celsius, desconhecida). */
function emBase(e: unknown): { dim: Dimensoes; escala: number } | null {
  if (!e || typeof e !== 'object') return null
  const o = e as Record<string, unknown>
  if (String(o.type ?? '').toUpperCase() !== 'IFCSIUNIT') return null
  const nome = texto(o.Name)
  const base = SI_EM_BASE[nome]
  if (!base) return null
  const [dim, escalaBase] = base
  const prefixo = texto(o.Prefix)
  const p = prefixo ? EXPOENTE_DO_PREFIXO[prefixo] : 0
  if (p === undefined) return null
  // Numa área ou volume o prefixo aplica-se ao metro: mm² = (1e-3 m)².
  const vezes = nome === 'SQUARE_METRE' ? 2 : nome === 'CUBIC_METRE' ? 3 : 1
  return { dim, escala: escalaBase * 10 ** (p * vezes) }
}

/** A forma habitual de uma derivada, se a conta fechar exactamente. */
export function equivalenciaDaDerivada(
  elementos: Array<{ unidade: unknown; expoente: number }>,
  tipo: string,
): string | null {
  const soma: Dimensoes = {}
  let escala = 1
  for (const { unidade, expoente: n } of elementos) {
    const b = emBase(unidade)
    if (!b || !Number.isFinite(n)) return null
    for (const k of BASES) soma[k] = (soma[k] ?? 0) + (b.dim[k] ?? 0) * n
    escala *= b.escala ** n
  }
  if (Math.abs(escala - 1) > 1e-9) return null
  const doTipo = FORMAS_HABITUAIS.find((f) => f.tipos?.includes(tipo) && mesmasDimensoes(f.dim, soma))
  const geral = FORMAS_HABITUAIS.find((f) => !f.tipos && mesmasDimensoes(f.dim, soma))
  return (doTipo ?? geral)?.simbolo ?? null
}

/** Uma unidade a partir da entidade do web-ifc. Null se não for uma. */
export function lerUnidade(e: unknown): Unidade | null {
  const u = lerUnidadeSemOrigem(e)
  if (!u) return null
  const o = e as Record<string, unknown>
  if (typeof o.expressID === 'number') {
    u.origem = { id: o.expressID, classe: String(o.type ?? '') }
  }
  return u
}

function lerUnidadeSemOrigem(e: unknown): Unidade | null {
  if (!e || typeof e !== 'object') return null
  const o = e as Record<string, unknown>
  const classe = String(o.type ?? '').toUpperCase()
  if (classe === 'IFCSIUNIT') {
    const prefixo = texto(o.Prefix)
    const nome = texto(o.Name)
    return {
      tipo: texto(o.UnitType),
      classe: 'SI',
      simbolo: simboloSI(prefixo, nome),
      nome: nomeSI(prefixo, nome),
    }
  }
  if (classe === 'IFCCONVERSIONBASEDUNIT' || classe === 'IFCCONVERSIONBASEDUNITWITHOFFSET') {
    const nome = texto(o.Name)
    const conhecido = CONVERSOES[nome.toUpperCase()]
    const factor = o.ConversionFactor as Record<string, unknown> | undefined
    const base = lerUnidade(factor?.UnitComponent)
    const v = Number(valor(factor?.ValueComponent))
    const simbolo = conhecido?.[0] ?? nome.toLowerCase()
    return {
      tipo: texto(o.UnitType),
      classe: 'conversao',
      simbolo,
      nome: conhecido?.[1] ?? nome.toLowerCase(),
      detalhe:
        base && Number.isFinite(v)
          ? `1 ${simbolo} = ${formatarFactor(v)} ${base.simbolo}`
          : undefined,
    }
  }
  if (classe === 'IFCDERIVEDUNIT') {
    const elementos = Array.isArray(o.Elements) ? o.Elements : []
    const partes: string[] = []
    const paraConta: Array<{ unidade: unknown; expoente: number }> = []
    for (const el of elementos) {
      const eo = el as Record<string, unknown>
      const u = lerUnidadeSemOrigem(eo.Unit)
      const n = Number(valor(eo.Exponent))
      if (!u || !Number.isFinite(n)) continue
      partes.push(`${u.simbolo}${expoente(n)}`)
      paraConta.push({ unidade: eo.Unit, expoente: n })
    }
    const tipo = texto(o.UnitType)
    const definido = texto(o.UserDefinedType)
    const tipoFinal = tipo === 'USERDEFINED' ? `USERDEFINED:${definido || 'definida pelo autor'}` : tipo
    const simbolo = partes.join('·') || '?'
    const eq = paraConta.length === partes.length ? equivalenciaDaDerivada(paraConta, tipo) : null
    return {
      tipo: tipoFinal,
      classe: 'derivada',
      simbolo,
      nome: nomeDoTipo(tipoFinal),
      detalhe: `derivada: ${simbolo}`,
      equivalencia: eq && eq !== simbolo ? eq : undefined,
    }
  }
  if (classe === 'IFCMONETARYUNIT') {
    const moeda = texto(o.Currency)
    return { tipo: 'MONETARYUNIT', classe: 'monetaria', simbolo: moeda, nome: moeda }
  }
  if (classe === 'IFCCONTEXTDEPENDENTUNIT') {
    const nome = texto(o.Name)
    return { tipo: texto(o.UnitType), classe: 'contexto', simbolo: nome, nome }
  }
  return null
}

function formatarFactor(v: number): string {
  const t = Math.abs(v) >= 0.001 && Math.abs(v) < 1e6 ? String(Number(v.toPrecision(6))) : v.toExponential(4)
  return t.replace('.', ',')
}

/** As unidades do projecto, uma por tipo.
 *
 *  O IFC proíbe duas unidades do mesmo tipo no IfcUnitAssignment (regra
 *  WR01). Se o ficheiro o fizer, não se escolhe nenhuma: o tipo vai para
 *  `ambiguas`, com todas as que o ficheiro declara, e as propriedades
 *  desse tipo ficam com unidade ambígua. Escolher a primeira seria
 *  decidir por quem fez o ficheiro. */
export function unidadesDoProjecto(entidades: unknown[]): {
  porTipo: Map<string, Unidade>
  ambiguas: Map<string, Unidade[]>
} {
  const todas = new Map<string, Unidade[]>()
  for (const e of entidades) {
    const u = lerUnidade(e)
    if (!u) continue
    const lista = todas.get(u.tipo) ?? []
    lista.push(u)
    todas.set(u.tipo, lista)
  }
  const porTipo = new Map<string, Unidade>()
  const ambiguas = new Map<string, Unidade[]>()
  for (const [tipo, lista] of todas) {
    if (lista.length === 1) porTipo.set(tipo, lista[0])
    else ambiguas.set(tipo, lista)
  }
  return { porTipo, ambiguas }
}

/** Os tipos que quase todo o modelo usa. Se o projecto não os declarar,
 *  diz-se. */
export const TIPOS_ESSENCIAIS = ['LENGTHUNIT', 'AREAUNIT', 'VOLUMEUNIT', 'PLANEANGLEUNIT']

/** Medidas sem dimensão: não levam unidade. */
const ADIMENSIONAIS = new Set([
  'IFCRATIOMEASURE',
  'IFCPOSITIVERATIOMEASURE',
  'IFCNORMALISEDRATIOMEASURE',
  'IFCCOUNTMEASURE',
  'IFCNUMERICMEASURE',
  'IFCPARAMETERVALUE',
  'IFCCONTEXTDEPENDENTMEASURE',
  'IFCDESCRIPTIVEMEASURE',
  'IFCQUANTITYCOUNT',
  'IFCQUANTITYNUMBER',
])

/** Medidas cujo tipo de unidade não se deduz do nome. */
const MEDIDAS_ESPECIAIS: Record<string, string> = {
  IFCTHERMALCONDUCTIVITYMEASURE: 'THERMALCONDUCTANCEUNIT',
  IFCQUANTITYLENGTH: 'LENGTHUNIT',
  IFCQUANTITYAREA: 'AREAUNIT',
  IFCQUANTITYVOLUME: 'VOLUMEUNIT',
  IFCQUANTITYWEIGHT: 'MASSUNIT',
  IFCQUANTITYTIME: 'TIMEUNIT',
}

/**
 * O tipo de unidade de um valor, pelo tipo de medida. `null` quando o
 * valor não tem unidade (texto, booleano, identificador) e 'ADIMENSIONAL'
 * quando é um número sem dimensão (razão, contagem).
 */
export function tipoDaMedida(medida: string | null | undefined): string | 'ADIMENSIONAL' | null {
  if (!medida) return null
  const m = medida.toUpperCase()
  if (ADIMENSIONAIS.has(m)) return 'ADIMENSIONAL'
  if (MEDIDAS_ESPECIAIS[m]) return MEDIDAS_ESPECIAIS[m]
  const r = /^IFC(?:POSITIVE|NONNEGATIVE)?([A-Z]+)MEASURE$/.exec(m)
  if (!r) return null
  return `${r[1]}UNIT`
}

export type EstadoDaUnidade = 'propria' | 'projecto' | 'ambigua' | 'em-falta' | 'adimensional' | 'sem-unidade'

export interface UnidadeDaPropriedade {
  estado: EstadoDaUnidade
  unidade: Unidade | null
  /** O tipo de unidade em causa, quando há. */
  tipo: string | null
  /** Quando é ambígua: as unidades que o projecto declara para o tipo. */
  candidatas?: Unidade[]
}

/**
 * A unidade de uma propriedade.
 *
 * @param medida  o tipo do valor (IFCLENGTHMEASURE, IFCLABEL, ...) ou a
 *                classe da quantidade (IFCQUANTITYLENGTH)
 * @param propria a unidade que a propriedade declara, se declarar
 * @param projecto as unidades do projecto, por tipo
 */
export function unidadeDaPropriedade(
  medida: string | null | undefined,
  propria: Unidade | null,
  projecto: Map<string, Unidade>,
  ambiguas: Map<string, Unidade[]> = new Map(),
): UnidadeDaPropriedade {
  if (propria) return { estado: 'propria', unidade: propria, tipo: propria.tipo }
  const tipo = tipoDaMedida(medida)
  if (tipo === null) return { estado: 'sem-unidade', unidade: null, tipo: null }
  if (tipo === 'ADIMENSIONAL') return { estado: 'adimensional', unidade: null, tipo: null }
  const u = projecto.get(tipo)
  if (u) return { estado: 'projecto', unidade: u, tipo }
  const candidatas = ambiguas.get(tipo)
  if (candidatas) return { estado: 'ambigua', unidade: null, tipo, candidatas }
  return { estado: 'em-falta', unidade: null, tipo }
}

/** A unidade em texto curto, com a entidade de onde veio: "mm (#11)".
 *  Null quando o valor não leva unidade (texto, razão, contagem). */
export function rotuloDaUnidade(u: UnidadeDaPropriedade): string | null {
  switch (u.estado) {
    case 'propria':
    case 'projecto':
      return u.unidade!.origem ? `${u.unidade!.simbolo} (#${u.unidade!.origem.id})` : u.unidade!.simbolo
    case 'ambigua':
      return 'unidade ambígua'
    case 'em-falta':
      return 'unidade não declarada'
    default:
      return null
  }
}

/** A explicação, para o título ao passar o rato. */
/** De onde veio a unidade e, nas derivadas, a forma habitual, dito de
 *  maneira a não se confundir o que está no ficheiro com o que se
 *  calculou. */
function procedencia(u: Unidade): string {
  const partes: string[] = []
  if (u.origem) partes.push(`No ficheiro: ${textoDaOrigem(u)}.`)
  if (u.equivalencia) {
    partes.push(`Equivale a ${u.equivalencia} (calculado a partir das unidades de base; o ficheiro declara ${u.simbolo}).`)
  }
  return partes.length ? ` ${partes.join(' ')}` : ''
}

export function explicarUnidade(u: UnidadeDaPropriedade): string {
  switch (u.estado) {
    case 'propria':
      return `Unidade declarada na própria propriedade: ${u.unidade!.nome} (${u.unidade!.simbolo}).${procedencia(u.unidade!)}`
    case 'projecto':
      return `A propriedade não declara unidade; vale a do projecto para ${nomeDoTipo(u.tipo!)}: ${u.unidade!.nome} (${u.unidade!.simbolo}).${procedencia(u.unidade!)}`
    case 'ambigua':
      return `A propriedade não declara unidade, e o projecto declara mais de uma para ${nomeDoTipo(u.tipo!)} (${(u.candidatas ?? [])
        .map((c) => `${c.simbolo}${c.origem ? ` em #${c.origem.id}` : ''}`)
        .join(', ')}), o que o IFC não permite. A unidade deste valor não se pode determinar.`
    case 'em-falta':
      return `Nem a propriedade nem o projecto declaram unidade de ${nomeDoTipo(u.tipo!)}. O valor não tem unidade definida.`
    case 'adimensional':
      return 'Valor sem dimensão (razão, contagem): não leva unidade.'
    case 'sem-unidade':
      return 'Texto, booleano ou identificador: não leva unidade.'
  }
}
